import {
  Business,
  BusinessOutlined,
  ChatOutlined,
  Close as CloseIcon,
  DragIndicator,
  FolderSpecial,
  MoreVert,
  PersonAdd,
  Public,
  RemoveCircleOutline,
  Search,
  Send,
  Store,
} from "@mui/icons-material";
import { EmployeePersona } from "../../types";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import axios from "axios";
import { User } from "../../utils";
import React, { memo, useEffect, useMemo, useState } from "react";
import { ListManager } from "react-beautiful-dnd-grid";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import capture from "../../capture";
import { Company, Employee } from "../../types";
import { hostname, parseError, uniqueId, addImageDimensions } from "../../utils";
import { hasAdminAccessByRole } from "../../utils/roleUtils";
import { useEmployeeTerminology } from "../../utils/terminology";
import ChatApi from "../chat/chatApi";
import QuoteRequestDialog from "../chat/dialogs/QuoteRequestDialog";
import DepartmentSelector from "../../components/DepartmentSelector";
import Departments from "../settings/Departments";
import EditEmployeeDialog from "./dialogs/EditEmployeeDialog";
import EmployeeTemplatesDialog from "./dialogs/EmployeeTemplatesDialog";
import NewEmployeeDialog from "./dialogs/NewEmployeeDialog";
import PublicEmployeeMarketplaceDialog from "./dialogs/PublicEmployeeMarketplaceDialog";
import { useWhitelabel } from "../../context/WhitelabelContext";

export default ({
  currentUser,
  companyId,
  getToken,
  eventListener,
  openDialog,
  company,
  refreshEmployees,
  currentCompanyPlan,
  mode,
  userRole,
  dashboardEmployees,
}: {
  currentUser: any | User;
  companyId: string;
  getToken: () => Promise<string>;
  eventListener: (event: any) => void;
  openDialog: (
    title: string,
    content: string,
    buttonText: string,
    onClick: () => void
  ) => void;
  company: Company;
  refreshEmployees: () => void;
  currentCompanyPlan: string;
  mode: string;
  userRole?: string | null;
  dashboardEmployees?: Array<Employee>;
}) => {
  const [employees, setEmployees] = useState<Array<Employee>>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [newEmployee, setNewEmployee] = useState<Employee>({
    uid: "",
    id: uniqueId(),
    title: "",
    name: "",
    prompt: "",
    companyId: "",
    tags: [],
    picture: "",
  });
  const [modalLoading, setModalLoading] = useState(false);
  const [init, setInit] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const navigate = useNavigate();
  const { whitelabelSettings } = useWhitelabel();
  const publicEmployeesEnabled = whitelabelSettings?.publicEmployeeFilter !== "none";
  // For whitelabel instances, default to false (hide templates). For main site (null settings), default to true.
  const showEmployeeTemplates = whitelabelSettings
    ? (whitelabelSettings.showEmployeeTemplates ?? false)
    : true;
  const [loading, setLoading] = useState(true);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(
    null
  );
  const [marketplaceDialogOpen, setMarketplaceDialogOpen] = useState(false);
  const [quoteDialogOpen, setQuoteDialogOpen] = useState(false);
  const [quoteEmployee, setQuoteEmployee] = useState<Employee | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [departmentsDialogOpen, setDepartmentsDialogOpen] = useState(false);
  const [assignDepartmentsDialogOpen, setAssignDepartmentsDialogOpen] = useState(false);
  const [newEmployeeDialogOpen, setNewEmployeeDialogOpen] = useState(false);
  const [templatesDialogOpen, setTemplatesDialogOpen] = useState(false);
  const [assignDepartmentsEmployee, setAssignDepartmentsEmployee] = useState<Employee | null>(null);
  const [assignDepartmentsIds, setAssignDepartmentsIds] = useState<string[]>([]);
  const [assignDepartmentsLoading, setAssignDepartmentsLoading] = useState(false);
  const [boxesPerRow, setBoxesPerRow] = useState(4);
  const [cardWidth, setCardWidth] = useState(300); // Set initial width to prevent tiny cards
  const containerRef = React.useRef<HTMLDivElement>(null);
  const chatApi = new ChatApi();
  chatApi.init(eventListener, getToken);

  // Memoize avatar elements based on employee picture URLs
  const avatarCache = useMemo(() => {
    const cache: { [key: string]: JSX.Element } = {};
    employees.forEach((employee) => {
      if (employee.picture && !cache[employee.picture]) {
        cache[employee.picture] = (
          <Avatar
            key={employee.picture}
            src={addImageDimensions(employee.picture, 136, 136)}
            style={{
              marginBottom: 10,
              height: 68,
              width: 68,
            }}
          />
        );
      }
    });
    return cache;
  }, [employees]);

  // Get custom terminology
  const {
    employee: employeeText,
    employees: employeesText,
    employeeLower,
  } = useEmployeeTerminology();

  // Ref for the outer wrapper to observe parent size changes
  const wrapperRef = React.useRef<HTMLDivElement>(null);

  // Handle responsive grid with fixed card widths
  useEffect(() => {
    let resizeTimeout: NodeJS.Timeout | null = null;
    let lastWidth = 0;

    const handleResize = () => {
      if (!containerRef.current) return;

      const containerWidth = containerRef.current.offsetWidth;
      // Skip if container has no width yet (not fully rendered)
      if (containerWidth === 0) return;

      // Skip if width hasn't actually changed (prevents unnecessary re-renders)
      if (containerWidth === lastWidth) return;
      lastWidth = containerWidth;

      let columns = 4;

      if (window.innerWidth < 600) {
        columns = 1; // xs
      } else if (window.innerWidth < 900) {
        columns = 2; // sm
      } else if (window.innerWidth < 1200) {
        columns = 3; // md
      } else {
        columns = 4; // lg
      }

      setBoxesPerRow(columns);

      // Calculate card width based on container width
      // Account for gaps between cards
      const gapWidth = 24; // 24px gap between cards
      const totalGaps = (columns - 1) * gapWidth; // Number of gaps is columns - 1
      const availableWidth = containerWidth - totalGaps;
      const calculatedWidth = availableWidth / columns;
      setCardWidth(calculatedWidth);
    };

    // Debounced resize handler for ResizeObserver
    // Uses longer delay to ensure CSS transitions have completed (drawer open/close)
    const debouncedResize = () => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      // Use 100ms debounce to handle sidebar CSS transitions smoothly
      resizeTimeout = setTimeout(handleResize, 100);
    };

    // Initial calculation with slight delay to ensure container is rendered
    const timer1 = setTimeout(handleResize, 0);
    // Secondary calculation to catch any layout shifts
    const timer2 = setTimeout(handleResize, 100);

    window.addEventListener("resize", handleResize);

    // Use ResizeObserver to detect container size changes (e.g., sidebar toggle, parent resize)
    const resizeObserver = new ResizeObserver((entries) => {
      // Only trigger if we have valid entries with non-zero dimensions
      for (const entry of entries) {
        if (entry.contentRect.width > 0) {
          debouncedResize();
          break;
        }
      }
    });

    // Observe the wrapper element (our component's outer container)
    if (wrapperRef.current) {
      resizeObserver.observe(wrapperRef.current);
    }

    // Also observe the parent element (the div with hidden attribute in Company.tsx)
    // This catches sidebar open/close events that resize the parent container
    const parentElement = wrapperRef.current?.parentElement;
    if (parentElement) {
      resizeObserver.observe(parentElement);
    }

    // Poll every 100ms to catch any size changes that ResizeObserver might miss
    // This handles edge cases like CSS transitions, dynamic content, or delayed layout
    const pollInterval = setInterval(() => {
      if (containerRef.current) {
        const currentWidth = containerRef.current.offsetWidth;
        if (currentWidth > 0 && currentWidth !== lastWidth) {
          handleResize();
        }
      }
    }, 100);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      clearInterval(pollInterval);
      window.removeEventListener("resize", handleResize);
      resizeObserver.disconnect();
    };
  }, [employees.length]); // Re-run when employees change

  const getEmployees = async (search: string = "") => {
    setLoading(true);
    const currentToken = await getToken();
    const params = search ? `?search=${encodeURIComponent(search)}` : "";
    await axios
      .get(`${hostname}/companies/${companyId}/employees${params}`, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      })
      .then((res) => {
        setEmployees(res.data.employees);
      })
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      });
    setInit(true);
    setLoading(false);
  };

  const resetModalState = () => {
    setModalOpen(false);
    setEditMode(false);
    setNewEmployee({
      uid: "",
      id: uniqueId(),
      title: "",
      name: "",
      prompt: "",
      companyId: "",
      tags: [],
      picture: "",
    });
  };

  const handleSaveEmployee = async (
    employee: Employee,
    publicSettings: any
  ) => {
    setModalLoading(true);
    const currentToken = await getToken();

    try {
      // Create employee object with all settings including public ones
      const employeeToSave = {
        ...employee,
        // Include public settings if employee is public, otherwise clear them
        ...(employee.public
          ? {
              contactEmail: publicSettings.contactEmail,
              description: publicSettings.description,
              ctaText: publicSettings.ctaText,
            }
          : {
              contactEmail: null,
              description: null,
              ctaText: null,
            }),
      };

      await axios.post(`${hostname}/employees/${employee.id}`, employeeToSave, {
        headers: {
          Authorization: `Bearer ${currentToken}`,
        },
      });

      setModalLoading(false);
      resetModalState();
      refreshEmployees();
      await getEmployees();
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
      setModalLoading(false);
    }
  };

  const handleCreateEmployee = async (
    employee: Employee,
    publicSettings: any
  ) => {
    capture("Create new employee", {});
    setModalLoading(true);
    const currentToken = await getToken();

    // Check which fields need AI generation
    const needsGeneration = !employee.name || !employee.prompt;

    if (needsGeneration) {
      // Use the existing AI generation for missing fields
      await axios
        .post(
          `${hostname}/employees/create-partial`,
          {
            companyId: companyId,
            title: employee.title,
            name: employee.name || null,
            prompt: employee.prompt || null,
            responseInstructions: employee.responseInstructions || null,
            tags: employee.tags,
            picture: employee.picture || null,
            departmentIds: employee.departmentIds || [],
          },
          {
            headers: {
              Authorization: `Bearer ${currentToken}`,
            },
          }
        )
        .catch((err) => {
          eventListener({
            type: "SET_ERROR",
            error: parseError(err),
          });
        });
    } else {
      // All fields are provided, save directly
      await axios
        .post(
          `${hostname}/employees/${employee.id}`,
          {
            ...employee,
            companyId: companyId,
            uid: currentUser.uid,
          },
          {
            headers: {
              Authorization: `Bearer ${currentToken}`,
            },
          }
        )
        .catch((err) => {
          eventListener({
            type: "SET_ERROR",
            error: parseError(err),
          });
        });
    }

    await getEmployees();
    setModalLoading(false);
    resetModalState();
    refreshEmployees();
  };

  const handleCreateFromScratch = () => {
    setEditMode(false);
    setNewEmployee({
      uid: "",
      id: uniqueId(),
      title: "",
      name: "",
      prompt: "",
      companyId: "",
      tags: [],
      picture: "",
    });
    setModalOpen(true);
  };

  const handleSelectPersona = async (persona: EmployeePersona) => {
    capture("Create employee from persona", { personaId: persona.id });
    setModalLoading(true);
    const currentToken = await getToken();

    try {
      const response = await axios.post(
        `${hostname}/employees/create-from-persona`,
        {
          companyId: companyId,
          personaId: persona.id,
        },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );

      if (response.data.success) {
        await getEmployees();
        refreshEmployees();
        eventListener({
          type: "SET_SUCCESS",
          message: `${employeeText} created from template`,
        });
      }
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setModalLoading(false);
    }
  };

  const submitQuoteRequest = async (quoteData: {
    contactName: string;
    contactEmail: string;
    contactPhone?: string;
    message?: string;
  }) => {
    if (!quoteEmployee) return;

    const token = await getToken();

    // Create a temporary chat for the quote request
    const tempChatId = uuidv4();

    await axios.post(
      `${hostname}/send-quote-email`,
      {
        companyId,
        employeeId: quoteEmployee.id,
        chatId: tempChatId,
        ...quoteData,
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );

    eventListener({
      type: "SET_SUCCESS",
      message:
        "Quote email sent! The service provider will receive your contact details.",
    });

    setQuoteDialogOpen(false);
    setQuoteEmployee(null);
  };

  const createChat = async (employee: Employee) => {
    capture("Created new chat", {});
    const currentToken = await getToken();
    const chatId = uuidv4();
    await axios
      .post(
        `${hostname}/chats`,
        {
          id: chatId,
          companyId: companyId,
          ts: new Date().getTime(),
          employeeId: employee.id,
        },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      )
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      });
    navigate("/dashboard/companies/" + companyId + "/chats/" + chatId);
  };

  const openAssignDepartmentsDialog = async (employee: Employee) => {
    setAssignDepartmentsEmployee(employee);
    setAssignDepartmentsIds(employee.departments?.map((d) => d.id) || []);
    setAssignDepartmentsDialogOpen(true);

    // Load current departments for this employee
    const currentToken = await getToken();
    try {
      const response = await axios.get(
        `${hostname}/employees/${employee.id}/departments?companyId=${companyId}`,
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );
      setAssignDepartmentsIds(response.data.map((dept: any) => dept.id));
    } catch (err) {
      console.error("Failed to load employee departments:", err);
    }
  };

  const saveAssignedDepartments = async () => {
    if (!assignDepartmentsEmployee) return;

    setAssignDepartmentsLoading(true);
    const currentToken = await getToken();

    try {
      await axios.put(
        `${hostname}/employees/${assignDepartmentsEmployee.id}/departments`,
        {
          companyId: companyId,
          departmentIds: assignDepartmentsIds,
        },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );
      eventListener({
        type: "SET_SUCCESS",
        message: "Departments updated successfully.",
      });
      setAssignDepartmentsDialogOpen(false);
      refreshEmployees();
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setAssignDepartmentsLoading(false);
    }
  };

  const deleteEmployee = async (employee: Employee) => {
    const currentToken = await getToken();
    const isPublicEmployee = employee.isPublicEmployee;

    openDialog(
      isPublicEmployee ? `Remove ${employeeText}` : `Delete ${employeeText}`,
      isPublicEmployee
        ? `Are you sure you want to remove ${employee.name} from your company?`
        : `Are you sure you want to delete ${employeeLower} ${employee.name}?`,
      "Confirm",
      () => {
        setLoading(true);
        axios
          .delete(
            `${hostname}/employees/${employee.id}?companyId=${companyId}`,
            {
              headers: {
                Authorization: `Bearer ${currentToken}`,
              },
            }
          )
          .then(() => {
            eventListener({
              type: "SET_SUCCESS",
              message: isPublicEmployee
                ? `${employee.name} has been removed from your company.`
                : `${employee.name} has been deleted.`,
            });
          })
          .catch((err) => {
            eventListener({
              type: "SET_ERROR",
              error: parseError(err),
            });
          })
          .finally(() => {
            getEmployees();
          });
      }
    );
  };

  const updatePhoto = async () => {
    setPhotoLoading(true);
    const currentToken = await getToken();
    await axios
      .post(
        `${hostname}/employees/${newEmployee.id}/picture`,
        { companyId: companyId },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      )
      .then((res) =>
        setNewEmployee({ ...newEmployee, picture: res.data.picture })
      )
      .catch((err) => {
        eventListener({
          type: "SET_ERROR",
          error: parseError(err),
        });
      });
    setPhotoLoading(false);
  };

  const uploadCustomPhoto = async (file: File) => {
    setUploadLoading(true);
    try {
      const currentToken = await getToken();

      // Convert image to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        try {
          const base64String = reader.result as string;

          // Create image element to get dimensions
          const img = new Image();
          img.onload = async () => {
            const imageData = {
              name: `${newEmployee.name || "Employee"} Profile Picture`,
              category: "employee-profile",
              url: base64String,
              mimeType: file.type,
              width: img.width.toString(),
              height: img.height.toString(),
              fileSize: file.size.toString(),
              public: true,
            };

            // Upload the image to platform-images
            const uploadResponse = await axios.post(
              `${hostname}/platform-images`,
              imageData,
              {
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${currentToken}`,
                },
              }
            );

            if (uploadResponse.status === 201) {
              const responseData = uploadResponse.data;
              // Use publicUrl from backend response (respects whitelabel domains)
              const publicUrl = responseData.publicUrl || responseData.url;

              // Only save to database if in edit mode (employee already exists)
              if (editMode) {
                await axios.post(
                  `${hostname}/employees/${newEmployee.id}`,
                  {
                    ...newEmployee,
                    picture: publicUrl,
                    companyId: companyId,
                  },
                  {
                    headers: {
                      Authorization: `Bearer ${currentToken}`,
                    },
                  }
                );
              }

              // Update local state with the new picture URL
              setNewEmployee({ ...newEmployee, picture: publicUrl });
            }
          };
          img.src = base64String;
        } catch (err) {
          console.error("Error uploading custom photo:", err);
          eventListener({
            type: "SET_ERROR",
            error: "Failed to upload image. Please try again.",
          });
        } finally {
          setUploadLoading(false);
        }
      };

      reader.readAsDataURL(file);
    } catch (err) {
      console.error("Error reading file:", err);
      eventListener({
        type: "SET_ERROR",
        error: "Failed to read image file. Please try again.",
      });
      setUploadLoading(false);
    }
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];

    // Validate file type
    if (!file.type.startsWith("image/")) {
      eventListener({
        type: "SET_ERROR",
        error: "Please upload an image file (PNG, JPG, etc.)",
      });
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      eventListener({
        type: "SET_ERROR",
        error: "Image size should be less than 2MB",
      });
      return;
    }

    uploadCustomPhoto(file);
  };

  const handleDragEnd = async (
    sourceIndex: number,
    destinationIndex: number
  ) => {
    // Reorder items
    const items = Array.from(employees);
    const [reorderedItem] = items.splice(sourceIndex, 1);
    items.splice(destinationIndex, 0, reorderedItem);

    const reorderedEmployees = items.map((item: Employee, index: number) => ({
      ...item,
      displayOrder: index,
    }));

    // Update local state immediately for better UX
    setEmployees(reorderedEmployees);

    // Send update to backend
    try {
      const currentToken = await getToken();
      const employeeOrders = reorderedEmployees.map((emp, index) => ({
        id: emp.id,
        displayOrder: index,
      }));

      await axios.post(
        `${hostname}/companies/${companyId}/employees/order`,
        {
          employees: employeeOrders,
        },
        {
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
        }
      );
    } catch (err) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
      // Revert on error
      getEmployees();
    }
  };

  // Debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500); // 500ms debounce delay

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  // Use dashboard employees when no search is active
  useEffect(() => {
    if (dashboardEmployees && !debouncedSearchQuery) {
      setEmployees(dashboardEmployees);
      setInit(true);
      setLoading(false);
    }
  }, [dashboardEmployees, debouncedSearchQuery]);

  // Fetch employees when debounced search changes (only for search queries)
  useEffect(() => {
    if (currentUser.uid && init && debouncedSearchQuery) {
      getEmployees(debouncedSearchQuery);
    }
  }, [debouncedSearchQuery]);

  // Initial load - only fetch if no dashboard employees provided
  useEffect(() => {
    if (currentUser.uid && !dashboardEmployees) {
      getEmployees();
    }
  }, [currentUser.uid, companyId]);

  // Memoized card renderer to prevent re-downloading images
  const renderEmployeeCard = useMemo(() => {
    return (employee: Employee) => {
      const index = employees.findIndex((e) => e.id === employee.id);
      const isFirstInRow = index % boxesPerRow === 0;
      const isLastInRow =
        (index + 1) % boxesPerRow === 0 || index === employees.length - 1;

      return (
        <Box
          key={employee.id}
          style={{
            width: `${cardWidth}px`,
            minWidth: `${cardWidth}px`,
            maxWidth: `${cardWidth}px`,
            paddingLeft: isFirstInRow ? 0 : "12px",
            paddingRight: isLastInRow ? 0 : "12px",
            paddingTop: "12px",
            paddingBottom: "12px",
            boxSizing: "content-box",
          }}
        >
          <Paper
            variant="outlined"
            onClick={() => {
              setEditMode(true);
              setNewEmployee(employee);
              setModalOpen(true);
            }}
            style={{
              padding: 10,
              cursor: "pointer",
              position: "relative",
              height: "100%",
            }}
          >
            <Box
              style={{
                position: "absolute",
                top: 8,
                right: 8,
                cursor: "grab",
                color: "#666",
                zIndex: 10,
              }}
            >
              <DragIndicator fontSize="small" />
            </Box>
            {avatarCache[employee.picture] || (
              <Avatar
                src={addImageDimensions(employee.picture, 136, 136)}
                style={{
                  marginBottom: 10,
                  height: 68,
                  width: 68,
                }}
              />
            )}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: 0.5,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitLineClamp: 1,
                  lineClamp: 1,
                  WebkitBoxOrient: "vertical",
                  flex: 1,
                }}
              >
                {employee.name}
              </Typography>
              {employee.public && !employee.isPublicEmployee && (
                <Chip
                  size="small"
                  icon={<Public />}
                  label="Public"
                  color="primary"
                  variant="outlined"
                  sx={{ height: 20, fontSize: "0.65rem" }}
                />
              )}
              {employee.isPublicEmployee && (
                <Chip
                  size="small"
                  icon={<Business />}
                  label="External"
                  color="secondary"
                  variant="outlined"
                  sx={{ height: 20, fontSize: "0.65rem" }}
                />
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 0.5,
                minHeight: 24,
                mb: 0.5,
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitLineClamp: 1,
                  lineClamp: 1,
                  WebkitBoxOrient: "vertical",
                  flex: "1 1 auto",
                }}
              >
                {employee.title}
              </Typography>
              {employee.departments && employee.departments.length > 0 && (
                <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                  {employee.departments.slice(0, 2).map((dept) => (
                    <Chip
                      key={dept.id}
                      size="small"
                      label={dept.name}
                      sx={{
                        height: 16,
                        fontSize: "0.65rem",
                        bgcolor: dept.color || undefined,
                        ...(dept.color && {
                          color: "#fff",
                          "& .MuiChip-label": {
                            color: "#fff",
                            px: 0.75,
                          },
                        }),
                      }}
                    />
                  ))}
                  {employee.departments.length > 2 && (
                    <Chip
                      size="small"
                      label={`+${employee.departments.length - 2}`}
                      sx={{
                        height: 16,
                        fontSize: "0.65rem",
                        "& .MuiChip-label": {
                          px: 0.75,
                        },
                      }}
                    />
                  )}
                </Stack>
              )}
            </Box>
            <Divider style={{ marginTop: 5, marginBottom: 0 }} />
            <div style={{ height: 58, overflow: "hidden" }}>
              <Typography variant="caption">Tags:</Typography>
              <Stack direction="row" spacing={1}>
                {(employee.tags || []).map((tag) => (
                  <Chip key={tag} size="small" label={tag} />
                ))}
                {!employee.tags ||
                  (employee.tags.length === 0 && (
                    <Typography variant="caption">None</Typography>
                  ))}
              </Stack>
            </div>
            <Tooltip
              title={employee.description || employee.prompt || ""}
              arrow
              placement="top"
              enterDelay={500}
              componentsProps={{
                tooltip: {
                  sx: {
                    fontSize: "0.875rem",
                    maxWidth: 500,
                  },
                },
              }}
            >
              <div
                style={{
                  height: 80,
                  overflow: "hidden",
                  marginBottom: 10,
                  display: "-webkit-box",
                  WebkitLineClamp: 4,
                  lineClamp: 4,
                  WebkitBoxOrient: "vertical",
                }}
              >
                <Typography variant="body2">
                  {employee.description || employee.prompt}
                </Typography>
              </div>
            </Tooltip>
            <Button
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                createChat(employee);
              }}
            >
              <ChatOutlined sx={{ mr: 1 }} /> Chat
            </Button>
            {hasAdminAccessByRole(userRole ?? null) && (
              <IconButton
                sx={{
                  position: "absolute",
                  bottom: 8,
                  right: 8,
                }}
                onClick={(e: any) => {
                  e.stopPropagation();
                  setAnchorEl(e.currentTarget);
                  setSelectedId(employee.id);
                }}
              >
                <MoreVert />
              </IconButton>
            )}
            <Menu
              anchorEl={anchorEl}
              open={selectedId === employee.id}
              onClose={(e: any) => {
                e.stopPropagation();
                setAnchorEl(null);
                setSelectedId(null);
              }}
            >
              {employee.isPublicEmployee ? (
                <>
                  {employee.ctaText && (
                    <MenuItem
                      onClick={(e: any) => {
                        e.stopPropagation();
                        setQuoteEmployee(employee);
                        setQuoteDialogOpen(true);
                        setAnchorEl(null);
                        setSelectedId(null);
                      }}
                    >
                      <Send sx={{ mr: 1 }} />
                      {employee.ctaText}
                    </MenuItem>
                  )}
                  <MenuItem
                    onClick={(e: any) => {
                      e.stopPropagation();
                      setAnchorEl(null);
                      setSelectedId(null);
                      openAssignDepartmentsDialog(employee);
                    }}
                  >
                    <FolderSpecial sx={{ mr: 1 }} />
                    Assign Departments
                  </MenuItem>
                  <MenuItem
                    onClick={(e: any) => {
                      e.stopPropagation();
                      setAnchorEl(null);
                      setSelectedId(null);
                      deleteEmployee(employee);
                    }}
                  >
                    <RemoveCircleOutline sx={{ mr: 1 }} />
                    Remove from Company
                  </MenuItem>
                </>
              ) : (
                <>
                  <MenuItem
                    onClick={(e: any) => {
                      e.stopPropagation();
                      setEditMode(true);
                      setNewEmployee(employee);
                      setModalOpen(true);
                      setAnchorEl(null);
                      setSelectedId(null);
                    }}
                  >
                    Edit
                  </MenuItem>
                  <MenuItem
                    onClick={(e: any) => {
                      e.stopPropagation();
                      setAnchorEl(null);
                      setSelectedId(null);
                      deleteEmployee(employee);
                    }}
                  >
                    Delete
                  </MenuItem>
                </>
              )}
            </Menu>
          </Paper>
        </Box>
      );
    };
  }, [employees, boxesPerRow, cardWidth, selectedId, anchorEl, userRole]);

  if (!currentUser.uid) {
    return <></>;
  }

  return (
    <Box ref={wrapperRef}>
      <Box sx={{ display: "flex", gap: 2, mt: 3, flexWrap: "wrap" }}>
        {hasAdminAccessByRole(userRole ?? null) && (
          <>
            <Button
              variant="contained"
              onClick={() => {
                if (showEmployeeTemplates) {
                  setNewEmployeeDialogOpen(true);
                } else {
                  // Skip the dialog and go directly to create from scratch
                  handleCreateFromScratch();
                }
              }}
              startIcon={<PersonAdd />}
              data-tutorial="create-employee-btn"
            >
              Create {employeeText}
            </Button>

            {publicEmployeesEnabled && (
              <Button
                variant="outlined"
                onClick={() => setMarketplaceDialogOpen(true)}
                startIcon={<Store />}
                data-tutorial="browse-public-btn"
              >
                Browse Public {employeesText}
              </Button>
            )}

            <Button
              variant="outlined"
              onClick={() => setDepartmentsDialogOpen(true)}
              startIcon={<BusinessOutlined />}
              data-tutorial="manage-departments-btn"
            >
              Manage Departments
            </Button>
          </>
        )}
      </Box>

      <Box
        sx={{
          position: "sticky",
          top: {
            xs: "calc(56px + env(safe-area-inset-top, 0px))",
            sm: "calc(64px + env(safe-area-inset-top, 0px))",
          },
          zIndex: 10,
          backgroundColor: "background.default",
          pt: 2,
          pb: 2,
          mx: -4,
          px: 4,
        }}
        data-tutorial="search-employees"
      >
        <TextField
          fullWidth
          placeholder={`Search ${employeesText.toLowerCase()} by name, role, description, or tags...`}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          size="small"
        />
      </Box>

      <Box sx={{ height: 4, mb: 1 }}>{loading && <LinearProgress />}</Box>

      {employees.length > 0 && (
        <Box ref={containerRef} style={{ marginTop: 0, marginBottom: 20 }} data-tutorial="employee-grid">
          {hasAdminAccessByRole(userRole ?? null) ? (
            <ListManager
              key={`listmanager-${boxesPerRow}`}
              items={employees}
              direction="horizontal"
              maxItems={boxesPerRow}
              onDragEnd={handleDragEnd}
              render={renderEmployeeCard}
            />
          ) : (
            <Grid container spacing={3}>
              {employees.map((employee) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={employee.id}>
                  <Paper
                    variant="outlined"
                    style={{
                      padding: 10,
                      position: "relative",
                    }}
                  >
                    <Avatar
                      src={addImageDimensions(employee.picture, 136, 136)}
                      style={{
                        marginBottom: 10,
                        height: 68,
                        width: 68,
                      }}
                    ></Avatar>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitLineClamp: 1,
                          lineClamp: 1,
                          WebkitBoxOrient: "vertical",
                          flex: 1,
                        }}
                      >
                        {employee.name}
                      </Typography>
                      {employee.public && !employee.isPublicEmployee && (
                        <Chip
                          size="small"
                          icon={<Public />}
                          label="Public"
                          color="primary"
                          variant="outlined"
                          sx={{ height: 20, fontSize: "0.65rem" }}
                        />
                      )}
                      {employee.isPublicEmployee && (
                        <Chip
                          size="small"
                          icon={<Business />}
                          label="External"
                          color="secondary"
                          variant="outlined"
                          sx={{ height: 20, fontSize: "0.65rem" }}
                        />
                      )}
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 0.5,
                        minHeight: 24,
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          overflow: "hidden",
                          display: "-webkit-box",
                          WebkitLineClamp: 1,
                          lineClamp: 1,
                          WebkitBoxOrient: "vertical",
                          flex: "1 1 auto",
                        }}
                      >
                        {employee.title}
                      </Typography>
                      {employee.departments &&
                        employee.departments.length > 0 && (
                          <Stack
                            direction="row"
                            spacing={0.5}
                            sx={{ flexShrink: 0 }}
                          >
                            {employee.departments.slice(0, 2).map((dept) => (
                              <Chip
                                key={dept.id}
                                size="small"
                                label={dept.name}
                                sx={{
                                  height: 16,
                                  fontSize: "0.65rem",
                                  bgcolor: dept.color || undefined,
                                  ...(dept.color && {
                                    color: "#fff",
                                    "& .MuiChip-label": {
                                      color: "#fff",
                                      px: 0.75,
                                    },
                                  }),
                                }}
                              />
                            ))}
                            {employee.departments.length > 2 && (
                              <Chip
                                size="small"
                                label={`+${employee.departments.length - 2}`}
                                sx={{
                                  height: 16,
                                  fontSize: "0.65rem",
                                  "& .MuiChip-label": {
                                    px: 0.75,
                                  },
                                }}
                              />
                            )}
                          </Stack>
                        )}
                    </Box>
                    <Divider style={{ marginTop: 5, marginBottom: 0 }} />
                    <div style={{ height: 58, overflow: "hidden" }}>
                      <Typography variant="caption">Tags:</Typography>
                      <Stack direction="row" spacing={1}>
                        {(employee.tags || []).map((tag) => (
                          <Chip key={tag} size="small" label={tag} />
                        ))}
                        {!employee.tags ||
                          (employee.tags.length === 0 && (
                            <Typography variant="caption">None</Typography>
                          ))}
                      </Stack>
                    </div>
                    <Tooltip
                      title={employee.description || employee.prompt || ""}
                      arrow
                      placement="top"
                      enterDelay={500}
                      componentsProps={{
                        tooltip: {
                          sx: {
                            fontSize: "0.875rem",
                            maxWidth: 500,
                          },
                        },
                      }}
                    >
                      <div
                        style={{
                          height: 80,
                          overflow: "hidden",
                          marginBottom: 10,
                          display: "-webkit-box",
                          WebkitLineClamp: 4,
                          lineClamp: 4,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        <Typography variant="body2">
                          {employee.description || employee.prompt}
                        </Typography>
                      </div>
                    </Tooltip>
                    <Button
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        createChat(employee);
                      }}
                    >
                      <ChatOutlined sx={{ mr: 1 }} /> Chat
                    </Button>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}
      {/* New Employee Selection Dialog */}
      <NewEmployeeDialog
        open={newEmployeeDialogOpen}
        onClose={() => setNewEmployeeDialogOpen(false)}
        onCreateFromScratch={handleCreateFromScratch}
        onBrowseTemplates={() => setTemplatesDialogOpen(true)}
      />

      {/* Employee Templates Dialog */}
      <EmployeeTemplatesDialog
        open={templatesDialogOpen}
        onClose={() => setTemplatesDialogOpen(false)}
        onSelectPersona={handleSelectPersona}
        getToken={getToken}
      />

      <EditEmployeeDialog
        open={modalOpen}
        onClose={resetModalState}
        employee={newEmployee}
        editMode={editMode}
        onSave={editMode ? handleSaveEmployee : handleCreateEmployee}
        onUpdatePhoto={updatePhoto}
        onFileUpload={handleFileUpload}
        photoLoading={photoLoading}
        uploadLoading={uploadLoading}
        modalLoading={modalLoading}
        companyId={companyId}
        companyName={company.name}
        getToken={getToken}
        currentCompanyPlan={currentCompanyPlan}
        eventListener={eventListener}
      />

      {/* Public Employee Marketplace Dialog */}
      <PublicEmployeeMarketplaceDialog
        open={marketplaceDialogOpen}
        onClose={() => setMarketplaceDialogOpen(false)}
        companyId={companyId}
        getToken={getToken}
        onEmployeeAdded={() => {
          getEmployees();
          refreshEmployees();
        }}
        eventListener={eventListener}
      />

      {/* Quote Request Dialog */}
      <QuoteRequestDialog
        open={quoteDialogOpen}
        onClose={() => {
          setQuoteDialogOpen(false);
          setQuoteEmployee(null);
        }}
        employee={quoteEmployee}
        chatId={""} // We'll create a temp chat in submitQuoteRequest
        companyId={companyId}
        onSubmitQuoteRequest={submitQuoteRequest}
      />

      {/* Departments Management Dialog */}
      <Dialog
        open={departmentsDialogOpen}
        onClose={() => setDepartmentsDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Manage Departments
          <IconButton
            aria-label="close"
            onClick={() => setDepartmentsDialogOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Departments
            company={company}
            companyId={companyId}
            getToken={getToken}
            currentUser={currentUser}
            eventListener={eventListener}
            openDialog={openDialog}
            userRole={userRole}
          />
        </DialogContent>
      </Dialog>

      {/* Assign Departments Dialog for Public Employees */}
      <Dialog
        open={assignDepartmentsDialogOpen}
        onClose={() => setAssignDepartmentsDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Assign Departments
          <IconButton
            aria-label="close"
            onClick={() => setAssignDepartmentsDialogOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {assignDepartmentsEmployee && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Assign {assignDepartmentsEmployee.name} to departments in your
                company.
              </Typography>
              <DepartmentSelector
                companyId={companyId}
                getToken={getToken}
                selectedDepartmentIds={assignDepartmentsIds}
                onChange={setAssignDepartmentsIds}
                label="Departments"
              />
              <Box sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 1 }}>
                <Button
                  onClick={() => setAssignDepartmentsDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={saveAssignedDepartments}
                  disabled={assignDepartmentsLoading}
                >
                  {assignDepartmentsLoading ? "Saving..." : "Save"}
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};
